import React, { useState } from 'react';
import { useForm, useWatch, type Control, type FieldValues } from 'react-hook-form';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { FormSharedUserInput } from 'src/components/common/forms/FormSharedUserInput';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import {
  useBusinessUnitAdminControllerBulkAddMembershipsMutation,
  useBusinessUnitAdminControllerFindGroupsQuery,
} from 'src/store/leela-api';

interface AddUserModalProps {
  businessUnitId: string;
  open: boolean;
  onClose: () => void;
}

interface AddUserFormData {
  users: string[];
  selectedGroupIds: string[];
  expiresAt: string;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ businessUnitId, open, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [bulkAddMembers, { isLoading: isSubmitting }] = useBusinessUnitAdminControllerBulkAddMembershipsMutation();
  const { data: groupsData } = useBusinessUnitAdminControllerFindGroupsQuery({
    businessUnitId,
    page: 1,
    pageSize: PERMISSION_LIST_PAGE_SIZE,
  });

  const groups = (groupsData?.data ?? []) as Array<{
    id: string;
    name: string;
    role: string;
    status: string;
    partnerId?: string;
    countries?: string[];
    publishers?: string[];
    businessLines?: string[];
  }>;

  const activeGroups = groups.filter(group => group.status === 'ACTIVE');

  const { control, handleSubmit, reset, setValue, getValues } = useForm<AddUserFormData>({
    defaultValues: { users: [], selectedGroupIds: [], expiresAt: '' },
  });

  const users = useWatch({ control, name: 'users' }) as string[];
  const selectedGroupIds = useWatch({ control, name: 'selectedGroupIds' }) as string[];
  const [expiryValue, setExpiryValue] = useState('');

  const toggleGroup = (groupId: string) => {
    const current = getValues('selectedGroupIds');
    if (current.includes(groupId)) {
      setValue('selectedGroupIds', current.filter(id => id !== groupId));
    } else {
      setValue('selectedGroupIds', [...current, groupId]);
    }
  };

  const onSubmit = async (data: AddUserFormData) => {
    if (data.users.length === 0 || data.selectedGroupIds.length === 0) return;

    try {
      for (const groupId of data.selectedGroupIds) {
        await bulkAddMembers({
          id: groupId,
          bulkCreateMembershipsDto: {
            members: data.users.map(userId => ({
              userId,
              ...(data.expiresAt && { expiresAt: data.expiresAt }),
            })),
          },
        }).unwrap();
      }
      createToast({
        type: AlertType.SUCCESS,
        message: t('add_user_success', {
          groupCount: data.selectedGroupIds.length,
          userCount: data.users.length,
        }),
      });
      handleClose();
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data?.message;
      createToast({ type: AlertType.ERROR, message: apiMessage ?? t('add_user_error') });
    }
  };

  const handleClose = () => {
    reset();
    setExpiryValue('');
    onClose();
  };

  return (
    <Modal
      data-testid="add-user-modal"
      open={open}
      scrollContainer="modal"
      title={t('add_user_title')}
      width={640}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Column spacing="400">
          <Column spacing="100">
            <Text type="b300">{t('add_user_users_label')}</Text>
            <Text color="secondary" type="b200">{t('add_user_users_help')}</Text>
            <FormSharedUserInput
              control={control as unknown as Control<FieldValues>}
              label=""
              name="users"
              placeholder={t('add_user_email_placeholder')}
              skipValidation
            />
          </Column>

          <Column spacing="100">
            <Text type="b300">{t('add_user_select_groups_label')}</Text>
            <Text color="secondary" type="b200">{t('add_user_select_groups_help')}</Text>
            <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #d5dbdb', borderRadius: '4px', padding: '8px' }}>
              {activeGroups.map(group => (
                <div
                  aria-pressed={selectedGroupIds?.includes(group.id)}
                  data-testid={`add-user-group-${group.id}`}
                  key={group.id}
                  role="button"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 4px' }}
                  tabIndex={0}
                  onClick={() => { toggleGroup(group.id); }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleGroup(group.id);
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedGroupIds?.includes(group.id)}
                    data-testid={`add-user-group-checkbox-${group.id}`}
                  />
                  <div data-testid={`add-user-group-details-${group.id}`} style={{ minWidth: 0 }}>
                    <ShrinkableTextValue testId={`add-user-group-name-${group.id}`} type="b300">
                      {group.name}
                    </ShrinkableTextValue>
                    <div style={{ fontSize: '12px', color: '#5f6b7a', marginTop: '2px' }}>
                      <div>{t('add_user_countries_label')} {group.countries?.length ? group.countries.join(', ') : t('add_user_all')}</div>
                      <div>{t('add_user_publishers_label')} {group.publishers?.length ? group.publishers.join(', ') : t('add_user_all')}</div>
                      <div>{t('add_user_business_lines_label')} {group.businessLines?.length ? group.businessLines.join(', ') : t('add_user_all')}</div>
                    </div>
                  </div>
                </div>
              ))}
              {activeGroups.length === 0 && (
                <Text color="secondary" type="b200">{t('add_user_no_active_groups')}</Text>
              )}
            </div>
          </Column>

          <Column spacing="100">
            <Text type="b300">{t('add_user_expiry_label')}</Text>
            <Text color="secondary" type="b200">{t('add_user_expiry_help')}</Text>
            <Input
              data-testid="add-user-expiry"
              placeholder={t('add_user_date_placeholder')}
              type="date"
              value={expiryValue}
              onChange={(value: string) => { setExpiryValue(value); setValue('expiresAt', value); }}
            />
          </Column>
        </Column>

        <div style={{ marginTop: '24px' }}>
          <ModalFooter>
            <Row alignmentHorizontal="end" spacing="300">
              <Button data-testid="add-user-cancel" type="secondary" onClick={handleClose}>
                {t('add_user_cancel')}
              </Button>
              <Button
                data-testid="add-user-submit"
                disabled={isSubmitting || !users?.length || !selectedGroupIds?.length}
                submit
                type="primary"
              >
                {isSubmitting ? <Loader size="small" /> : t('add_user_submit')}
              </Button>
            </Row>
          </ModalFooter>
        </div>
      </form>
    </Modal>
  );
};
